import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    doc,
    getDoc,
    updateDoc,
    setDoc,
    addDoc,
    serverTimestamp,
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, DocumentIcon, BanknotesIcon, PlusIcon, DocumentTextIcon, ClockIcon, CurrencyDollarIcon, MapPinIcon, UserCircleIcon, FunnelIcon, CheckCircleIcon, XMarkIcon, LockOpenIcon, LockClosedIcon, ArrowUpTrayIcon, LinkIcon, ClipboardDocumentIcon } from '@heroicons/react/20/solid';
import PrequalFinancialMetricsDisplay from './PrequalFinancialMetricsDisplay';
import LoanOfficerAssignment from './PrequalLoanOfficerAssignment';
import PrequalificationOcrReview from './PrequalificationOcrReview';
import PrequalDebtServiceCoverageCalculator from './prequalDebtServiceCoverageCalc';
import { generateSummaryPDF } from './PdfGenerator';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import PrequalificationTable from './PrequalificationTable';
import { generatePrequalSummaryPDF } from './PrequalSummaryPdfGenerator';

// Helper to get the appropriate dashboard URL based on current user
const getAdminDashboardUrl = () => {
  const currentUser = auth.currentUser;
  if (currentUser?.email?.toLowerCase() === 'ben@lended.ai') {
    return '/ben-portal';
  }
  return '/admindashboard';
};

const PrequalificationDetails = () => {
  const { prequalId } = useParams();
  const navigate = useNavigate();
  const [prequalData, setPrequalData] = useState(null);
  const [ocrData, setOcrData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isApplicantInfoExpanded, setIsApplicantInfoExpanded] = useState(true);
  const [isFinancialOverviewExpanded, setIsFinancialOverviewExpanded] = useState(true);
  const [isBusinessDetailsExpanded, setIsBusinessDetailsExpanded] = useState(true);
  const [isUploadedFilesExpanded, setIsUploadedFilesExpanded] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isEditingPrequalAmount, setIsEditingPrequalAmount] = useState(false);
  const [newPrequalAmount, setNewPrequalAmount] = useState('');
  const [isSavingAmount, setIsSavingAmount] = useState(false);
  const [isPrequalTableLocked, setIsPrequalTableLocked] = useState(true);
  const [isWorkbookUploadExpanded, setIsWorkbookUploadExpanded] = useState(true);
  const [workbookFile, setWorkbookFile] = useState(null);
  const [workbookUploading, setWorkbookUploading] = useState(false);
  const [workbookUploadProgress, setWorkbookUploadProgress] = useState(0);
  const [workbookUploadError, setWorkbookUploadError] = useState(null);
  const [showParsedModal, setShowParsedModal] = useState(false);
  const [parsedValues, setParsedValues] = useState(null);
  const [waitingForParse, setWaitingForParse] = useState(false);
  const [savingParsedValues, setSavingParsedValues] = useState(false);
  const [borrowerLinkCopied, setBorrowerLinkCopied] = useState(false);

  useEffect(() => {
    const fetchPrequalData = async () => {
      try {
        console.log("Fetching prequalification data for ID:", prequalId);
        
        // Get prequalification application data
        const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
        const prequalDocSnap = await getDoc(prequalDocRef);
        
        if (prequalDocSnap.exists()) {
          const data = { id: prequalDocSnap.id, ...prequalDocSnap.data() };
          console.log("Found prequalification data:", data);

          // Fetch broker name if brokerId exists
          if (data.brokerId && !data.brokerName) {
            try {
              const brokerDocRef = doc(db, 'brokers', data.brokerId);
              const brokerDocSnap = await getDoc(brokerDocRef);
              if (brokerDocSnap.exists()) {
                const brokerData = brokerDocSnap.data();
                const fullName = brokerData.firstName && brokerData.lastName
                  ? `${brokerData.firstName} ${brokerData.lastName}`
                  : brokerData.companyName || brokerData.name || brokerData.email || null;
                data.brokerName = fullName;
                data.brokerEmail = brokerData.email;
                data.brokerFirstName = brokerData.firstName || (fullName ? fullName.split(' ')[0] : '');
              }
            } catch (brokerErr) {
              console.error('Error fetching broker:', brokerErr);
            }
          }

          setPrequalData(data);

          // Get metadata for documents
          const metadataRef = collection(db, 'prequalificationMetadata');
          const metadataQuery = query(metadataRef,
            where('applicationId', '==', prequalId)
          );
          const metadataSnap = await getDocs(metadataQuery);
          const metadata = metadataSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log("Found metadata:", metadata);
          setUploadedFiles(metadata);
        }
      } catch (error) {
        console.error("Error fetching prequalification data:", error);
      }
    };

    const fetchOCRData = async () => {
      try {
        console.log("Fetching OCR data for prequalId:", prequalId);
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const q = query(ocrMetadataRef, 
          where('applicationId', '==', prequalId),
          where('documentName', '==', 'Business Tax Returns') // Updated to match the new structure
        );
        const querySnapshot = await getDocs(q);
        
        console.log("OCR query results:", querySnapshot.size);
        
        const processedData = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log("Processing OCR document:", data);
          
          // Extract year from label or filename
          let year = null;
          
          // Try to extract year from filename first
          const yearMatch = data.fileName?.match(/\d{4}/);
          if (yearMatch) {
            year = yearMatch[0];
          }
          
          // If no year in filename, try the label with improved matching
          if (!year && data.label) {
            // Handle various label formats like "2023 Business Tax Return" or "Business Tax Return 2023"
            const labelYearMatch = data.label.match(/\b(20\d{2})\b/);
            if (labelYearMatch) {
              year = labelYearMatch[1];
              console.log(`Extracted year ${year} from label: "${data.label}"`);
            }
          }
          
          // Check for structuredData field first, and use that if available
          if (year) {
            if (data.structuredData) {
              console.log(`Found year ${year} with structured data:`, data.structuredData);
              processedData[year] = data.structuredData;
            } else if (data.ocrResult) {
              console.log(`Found year ${year} with OCR result:`, data.ocrResult);
              processedData[year] = data.ocrResult;
            } else {
              console.log(`No OCR data found for year ${year}`);
            }
          } else {
            console.log(`Could not extract year from document with label: "${data.label}"`);
          }
        });
        
        console.log("Final processed OCR data:", processedData);
        setOcrData(processedData);
      } catch (error) {
        console.error("Error fetching OCR data:", error);
      }
    };

    fetchPrequalData();
    fetchOCRData();
  }, [prequalId]);

  const toggleApplicantInfo = () => setIsApplicantInfoExpanded(!isApplicantInfoExpanded);
  const toggleFinancialOverview = () => setIsFinancialOverviewExpanded(!isFinancialOverviewExpanded);
  const toggleBusinessDetails = () => setIsBusinessDetailsExpanded(!isBusinessDetailsExpanded);
  const toggleUploadedFiles = () => setIsUploadedFilesExpanded(!isUploadedFilesExpanded);

  const handleBack = () => {
    navigate(-1);
  };

  const borrowerSignupLink = `https://lended.ai/prequal-signup/${prequalId}`;

  const copyBorrowerSignupLink = async () => {
    try {
      await navigator.clipboard.writeText(borrowerSignupLink);
      setBorrowerLinkCopied(true);
      window.setTimeout(() => setBorrowerLinkCopied(false), 2000);
    } catch (error) {
      window.prompt('Copy this borrower signup link:', borrowerSignupLink);
    }
  };

  const handleAssignBank = async (bankId, dropboxLink) => {
    try {
      console.log('Starting bank assignment process...');
      console.log('Prequalification ID:', prequalId);
      console.log('Bank ID:', bankId);
      console.log('Dropbox Link:', dropboxLink);

      const now = new Date().toISOString();

      // Update the prequalification application
      const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
      const updateData = {
        assignedBankId: bankId,
        status: 'Assigned to Bank',
        lastUpdated: now,
        dropboxLink: dropboxLink
      };

      await updateDoc(prequalDocRef, updateData);
      console.log('Prequalification application updated successfully');

      // Create a new bank assignment
      const bankAssignmentRef = collection(db, 'bankAssignments');
      const newAssignment = {
        prequalificationId: prequalId,
        bankId: bankId,
        assignedAt: now,
        currentStage: {
          name: 'prequalification',
          enteredAt: now
        },
        stageHistory: [
          {
            name: 'prequalification',
            enteredAt: now,
            exitedAt: null
          }
        ],
        dropboxLink: dropboxLink
      };

      const docRef = await addDoc(bankAssignmentRef, newAssignment);
      console.log('Bank assignment created successfully with ID:', docRef.id);

      // Fetch bank data and create email
      const bankRef = doc(db, 'banks', bankId);
      const bankSnap = await getDoc(bankRef);
      if (bankSnap.exists()) {
        const bankData = bankSnap.data();
        console.log('Bank data retrieved:', bankData);
        
        await createEmailDocument(bankData);
      } else {
        console.error('Bank not found:', bankId);
      }

      // Update local state
      setPrequalData(prevData => ({
        ...prevData,
        assignedBankId: bankId,
        status: 'Assigned to Bank',
        lastUpdated: now,
        dropboxLink: dropboxLink
      }));

      console.log('Bank assignment process completed successfully');
    } catch (error) {
      console.error('Error assigning bank:', error);
      throw error;
    }
  };

  const createEmailDocument = async (bankData) => {
    try {
      console.log('Creating email document for bank:', bankData.bankName);
      const emailRef = collection(db, 'email');

      const loginUrl = "https://lended.ai/login"; // Replace with your actual login URL

      const emailData = {
        to: bankData.email,
        from: "Isak @ Lended <isak@lended.ai>",
        replyTo: "isak@lended.ai",
        subject: "You've been matched with a prequalification!",
        templateName: "PrequalMatch",
        message: {
          subject: "You've been matched with a prequalification!",
          firstName: bankData.loanOfficerName,
          prequalDetails: "A new prequalification application has been matched with your bank in the loan portal. Please log in to review the details.",
          html: `<p>Hey ${bankData.loanOfficerName},</p>

<p>A new prequalification application has been matched with your bank in the loan portal. Please <a href="${loginUrl}">log in</a> to review the details.</p>

<p>Looking forward to hearing back from you!</p>

<p>Best,</p>
<p>Isak</p>
<p>Lended</p>
`,
        },
      };
      console.log('Email data prepared:', JSON.stringify(emailData, null, 2));

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Email document created with ID:', emailDoc.id);
    } catch (error) {
      console.error('Error creating email document:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      throw error;
    }
  };

  const formatDate = (dateValue) => {
    if (dateValue instanceof Date) {
      return dateValue.toLocaleString();
    } else if (typeof dateValue === 'string') {
      return new Date(dateValue).toLocaleString();
    } else if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleString();
    }
    return 'N/A';
  };

  const calculateRevenue = (yearData) => {
    return yearData?.gross_receipts_or_sales || 0;
  };

  const calculateEBITDA = (yearData) => {
    const baseIncome = Number(yearData?.ordinary_business_income || 0);
    const interest = Number(yearData?.interest || 0);
    const depreciation = Number(yearData?.depreciation || 0);
    const amortization = Number(yearData?.amortization || 0);
    return baseIncome + interest + depreciation + amortization;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return 'N/A';
    
    // For display in the edit form, show the full number
    if (isEditingPrequalAmount) {
      return `$${num.toLocaleString()}`;
    }
    
    // For normal display, show in millions format
    const millions = num / 1000000;
    return `$${millions.toFixed(1)}mm`;
  };

  const handleEditPrequalAmount = () => {
    setNewPrequalAmount(prequalData?.prequalifiedAmount || '');
    setIsEditingPrequalAmount(true);
  };

  const handleSavePrequalAmount = async () => {
    try {
      setIsSavingAmount(true);
      
      // Validate input
      const amount = parseFloat(newPrequalAmount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        setIsSavingAmount(false);
        return;
      }
      
      // Update in Firestore - also set status to 'prequalified'
      const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
      await updateDoc(prequalDocRef, {
        prequalifiedAmount: amount,
        status: 'prequalified',
        lastUpdated: serverTimestamp()
      });

      // Update local state
      setPrequalData(prevData => ({
        ...prevData,
        prequalifiedAmount: amount,
        status: 'prequalified',
        lastUpdated: new Date()
      }));
      
      setIsEditingPrequalAmount(false);
      setIsSavingAmount(false);
    } catch (error) {
      console.error('Error saving prequalified amount:', error);
      alert('Error saving amount. Please try again.');
      setIsSavingAmount(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingPrequalAmount(false);
  };

  const handleGenerateSummaryPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Create PDF content with proper defaults for missing values
      const pdfContent = {
        businessName: prequalData.businessName || 'N/A',
        prequalifiedAmount: prequalData.prequalifiedAmount || 0,
        prequalificationType: prequalData.prequalificationType || 'Adjusted Purchase Price',
        prequalificationEquityPercent: prequalData.prequalificationEquityPercent || 10,
        brokerName: prequalData.brokerName || prequalData.broker || null,
        realEstateValue: prequalData.realEstateValue || prequalData.realEstateAppraisal || 0,
        ocrData: ocrData || {},
        prequalId: prequalId
      };
      
      console.log("Generating PDF with content:", pdfContent);
      
      // Generate PDF with error handling
      let pdfBlob;
      try {
        pdfBlob = await generatePrequalSummaryPDF(pdfContent);
      } catch (pdfError) {
        console.error('Error in PDF generation:', pdfError);
        setIsGeneratingPDF(false);
        alert('Error generating PDF. Please check console for details.');
        return;
      }
      
      if (!pdfBlob) {
        console.error('PDF generation returned empty blob');
        setIsGeneratingPDF(false);
        alert('Error: PDF generation failed to produce a document.');
        return;
      }
      
      // Upload PDF to Firebase Storage
      const fileName = `${prequalData.businessName || 'Business'}_Prequalification_Summary_${new Date().toISOString().replace(/:/g, '-')}.pdf`;
      const storageRef = ref(storage, `prequalifications/${prequalId}/summaries/${fileName}`);
      
      const uploadTask = uploadBytesResumable(storageRef, pdfBlob);
      
      uploadTask.on('state_changed', 
        (snapshot) => {
          // Progress monitoring
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
        }, 
        (error) => {
          console.error('Error uploading PDF:', error);
          setIsGeneratingPDF(false);
          alert('Error uploading PDF. Please try again.');
        }, 
        async () => {
          try {
            // Upload completed successfully
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Add the PDF to the documents array in Firestore
            const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
            const newDocument = {
              id: Date.now().toString(),
              fileName: fileName,
              fileUrl: downloadURL,
              fileType: 'application/pdf',
              uploadDate: new Date().toISOString(),
              documentType: 'Prequalification Summary'
            };
            
            // Update the documents array in Firestore with error handling
            try {
              if (prequalData.documents && Array.isArray(prequalData.documents)) {
                await updateDoc(prequalDocRef, {
                  documents: [...prequalData.documents, newDocument]
                });
                setUploadedFiles([...prequalData.documents, newDocument]);
              } else if (prequalData.documentMetadata && Array.isArray(prequalData.documentMetadata)) {
                await updateDoc(prequalDocRef, {
                  documentMetadata: [...prequalData.documentMetadata, newDocument]
                });
                setUploadedFiles([...prequalData.documentMetadata, newDocument]);
              } else {
                await updateDoc(prequalDocRef, {
                  documents: [newDocument]
                });
                setUploadedFiles([newDocument]);
              }
            } catch (firestoreError) {
              console.error('Error updating Firestore:', firestoreError);
              // Still open the PDF even if Firestore update fails
            }
            
            // Open the PDF in a new tab
            window.open(downloadURL, '_blank');
          } catch (finalError) {
            console.error('Error in final processing:', finalError);
          } finally {
            setIsGeneratingPDF(false);
          }
        }
      );
    } catch (error) {
      console.error('Error in overall PDF generation process:', error);
      setIsGeneratingPDF(false);
      alert('An error occurred. Please try again.');
    }
  };

  const togglePrequalTableLock = () => {
    setIsPrequalTableLocked(!isPrequalTableLocked);
  };

  const handleRequestUnlock = () => {
    // Scroll to the prequalification table section
    const prequalTableElement = document.getElementById('prequalification-table');
    if (prequalTableElement) {
      prequalTableElement.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Flash the unlock button to draw attention
    const lockButton = document.getElementById('toggle-lock-button');
    if (lockButton) {
      lockButton.classList.add('animate-pulse');
      setTimeout(() => {
        lockButton.classList.remove('animate-pulse');
      }, 2000);
    }
  };

  if (!prequalData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-300 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300 rounded"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentYear = Object.keys(ocrData).sort().pop();
  const currentYearData = ocrData[currentYear];

  const financialStats = [
    { name: 'Revenue', value: calculateRevenue(currentYearData), prefix: '$' },
    { name: 'EBITDA', value: calculateEBITDA(currentYearData), prefix: '$' },
    { name: 'Projected Sale Price', value: prequalData.projectedSalePrice, prefix: '$' },
    { name: 'Real Estate Appraisal', value: prequalData.realEstateAppraisal, prefix: '$' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <main className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with back button and PDF generation */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Prequalification Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                View and manage prequalification information for {prequalData.businessName || 'this business'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleBack}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </button>

              {prequalData.brokerId && (
                <a
                  href={
                    prequalData.chosenBuyer?.loanApplicationId
                      ? `/broker-deal/${prequalData.brokerId}?dealId=${prequalData.chosenBuyer.loanApplicationId}`
                      : `/broker-deal/${prequalData.brokerId}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title={prequalData.chosenBuyer?.loanApplicationId
                    ? "Opens the broker's full-deal upload flow for the chosen buyer so you can upload on the broker's behalf."
                    : "Opens the broker's deal upload flow so you can submit a deal under this listing on the broker's behalf."}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  Upload as Broker
                </a>
              )}

              <button
                onClick={handleGenerateSummaryPDF}
                disabled={isGeneratingPDF}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Generate Summary PDF
                  </>
                )}
              </button>
            </div>
          </div>

          <section className="mb-8 overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-sm">
            <div className="flex flex-col gap-5 bg-gradient-to-r from-indigo-50 via-white to-blue-50 px-6 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
                  <LinkIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">Borrower signup link</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
                    Send this link to a borrower for this specific listing. Their signup and documents will be attached to this prequalification automatically.
                  </p>
                </div>
              </div>

              <div className="flex w-full flex-col gap-3 sm:flex-row lg:max-w-xl">
                <input
                  type="text"
                  readOnly
                  value={borrowerSignupLink}
                  onFocus={(event) => event.target.select()}
                  aria-label="Borrower signup link"
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                <button
                  type="button"
                  onClick={copyBorrowerSignupLink}
                  className={`inline-flex shrink-0 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    borrowerLinkCopied
                      ? 'bg-emerald-600 focus:ring-emerald-500'
                      : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                  }`}
                >
                  {borrowerLinkCopied ? (
                    <CheckCircleIcon className="mr-2 h-5 w-5" />
                  ) : (
                    <ClipboardDocumentIcon className="mr-2 h-5 w-5" />
                  )}
                  {borrowerLinkCopied ? 'Copied' : 'Copy link'}
                </button>
                <a
                  href={borrowerSignupLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Open
                </a>
              </div>
            </div>
          </section>

          {/* Prequalification Amount Card */}
          <div className="bg-gradient-to-br from-white to-green-50 shadow-lg rounded-xl overflow-hidden mb-8 border border-green-100">
            <div className="px-6 py-8 sm:px-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold leading-7 text-gray-900 flex items-center">
                    <BanknotesIcon className="h-6 w-6 text-indigo-600 mr-2" />
                    {prequalData.businessName || 'Business'} 
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Prequalification Status: <span className={`font-medium ${
                      prequalData.status === 'Complete' ? 'text-green-600' :
                      prequalData.status === 'In progress' ? 'text-blue-600' :
                      prequalData.status === 'Assigned to Bank' ? 'text-purple-600' :
                      prequalData.status === 'buyer_selected' ? 'text-amber-600' :
                      prequalData.status === 'prequalified' ? 'text-emerald-600' :
                      'text-gray-600'
                    }`}>
                      {prequalData.status === 'buyer_selected' ? 'Buyer Selected' :
                       prequalData.status === 'prequalified' ? 'Prequalified' :
                       prequalData.status || 'Not started'}
                    </span>
                  </p>
                  {/* Show Chosen Buyer Badge */}
                  {prequalData.chosenBuyer && (
                    <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800">
                      <UserCircleIcon className="w-4 h-4 mr-1" />
                      <span className="text-xs font-medium">
                        Buyer: {prequalData.chosenBuyer.firstName} {prequalData.chosenBuyer.lastName}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 sm:mt-0">
                  {isEditingPrequalAmount ? (
                    <div className="bg-white rounded-lg shadow-sm border border-green-200 px-6 py-4">
                      <p className="text-sm text-gray-500 mb-1">Edit Prequalified Amount</p>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={newPrequalAmount}
                          onChange={(e) => setNewPrequalAmount(e.target.value)}
                          className="block w-48 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Enter amount"
                          disabled={isSavingAmount}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSavePrequalAmount}
                            disabled={isSavingAmount}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {isSavingAmount ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isSavingAmount}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center sm:justify-end">
                      <div className="bg-white rounded-lg shadow-sm border border-green-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-500 mb-1">Prequalified Amount</p>
                          <button
                            onClick={handleEditPrequalAmount}
                            className="ml-4 inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Edit
                          </button>
                        </div>
                        <p className="text-3xl font-bold text-green-600">
                          {prequalData.prequalifiedAmount 
                            ? `$${Number(prequalData.prequalifiedAmount).toLocaleString()}` 
                            : (prequalData.expectedPrequalAmount 
                                ? formatCurrency(prequalData.expectedPrequalAmount) 
                                : 'Not calculated yet')}
                        </p>
                        {prequalData.prequalifiedAmount && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                              {prequalData.prequalificationType || 'Standard Financing'}
                            </span>
                            {prequalData.prequalificationWithSalary && (
                              <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                                With $75k Salary
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Workbook Upload */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              {/* Gradient border effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-2xl" />
              <div className="relative m-[2px] bg-white rounded-[14px] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm">
                      <ArrowUpTrayIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Prequal Workbook</h3>
                      <p className="text-xs text-gray-500">Upload .xlsx, .xls, or .xlsm to auto-parse financials</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {prequalData?.workbookParsedAt && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20">
                        <CheckCircleIcon className="h-3.5 w-3.5" /> Parsed
                      </span>
                    )}
                    {prequalData?.workbookFileUploaded && !prequalData?.workbookParsedAt && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2.5 py-1 text-xs font-semibold text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                        <ClockIcon className="h-3.5 w-3.5" /> Awaiting Parse
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Drag & drop upload area */}
                  {!(prequalData?.workbookParsedAt && !prequalData?.workbookParseFailed) && (
                    <label className={`relative flex flex-col items-center justify-center w-full py-8 px-4 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 ${
                      workbookUploading || waitingForParse
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : 'border-indigo-200 bg-gradient-to-b from-indigo-50/50 to-white hover:border-indigo-400 hover:bg-indigo-50/80 hover:shadow-inner'
                    }`}>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.xlsm"
                        disabled={workbookUploading || waitingForParse}
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setWorkbookFile(file);
                          setWorkbookUploadError(null);
                          setWorkbookUploading(true);
                          setWorkbookUploadProgress(0);

                          const storageRef = ref(storage, `listings/${prequalId}/workbook/${file.name}`);
                          const uploadTask = uploadBytesResumable(storageRef, file);

                          uploadTask.on('state_changed',
                            (snapshot) => setWorkbookUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
                            (error) => { setWorkbookUploadError(error.message); setWorkbookUploading(false); },
                            async () => {
                              try {
                                await getDownloadURL(uploadTask.snapshot.ref);
                                const listingsDocRef = doc(db, 'listings', prequalId);
                                await setDoc(listingsDocRef, {
                                  workbookFileUploaded: false,
                                  workbookParsedAt: null,
                                  workbookParseFailed: null,
                                  workbookParseError: null,
                                  recordType: 'prequal',
                                  prequalId,
                                  businessName: prequalData?.businessName || '',
                                  brokerEmail: prequalData?.brokerEmail || '',
                                  brokerFirstName: prequalData?.brokerFirstName || prequalData?.brokerName?.split(' ')[0] || '',
                                  uploadedAt: serverTimestamp(),
                                }, { merge: true });
                                await updateDoc(listingsDocRef, { workbookFileUploaded: true });
                                await updateDoc(doc(db, 'prequalificationApplications', prequalId), { workbookFileUploaded: true, recordType: 'prequal' });
                                setWorkbookUploading(false);
                                setWaitingForParse(true);

                                const unsubscribe = onSnapshot(listingsDocRef, (snap) => {
                                  const data = snap.data();
                                  if (data?.workbookParsedAt) {
                                    unsubscribe();
                                    setWaitingForParse(false);
                                    setParsedValues({ ...data, _raw: true });
                                    setShowParsedModal(true);
                                  }
                                  if (data?.workbookParseFailed) {
                                    unsubscribe();
                                    setWaitingForParse(false);
                                    setWorkbookUploadError(data.workbookParseError || 'Parse failed');
                                  }
                                });
                                setTimeout(() => { unsubscribe(); setWaitingForParse(false); }, 120000);
                              } catch (err) { setWorkbookUploadError(err.message); setWorkbookUploading(false); }
                            }
                          );
                        }}
                      />
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100">
                          <ArrowUpTrayIcon className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-700">
                            Drop your workbook here or <span className="text-indigo-600">click to browse</span>
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Supports .xlsx, .xls, .xlsm</p>
                        </div>
                      </div>
                    </label>
                  )}

                  {/* Upload / parse progress */}
                  {(workbookUploading || waitingForParse) && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-700">{waitingForParse ? 'Parsing workbook...' : 'Uploading...'}</span>
                        {!waitingForParse && <span className="text-indigo-600 font-semibold">{workbookUploadProgress}%</span>}
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${waitingForParse ? 'bg-yellow-400 animate-pulse' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`}
                          style={{ width: waitingForParse ? '100%' : `${workbookUploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error display */}
                  {workbookUploadError && (
                    <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-4">
                      <XMarkIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{workbookUploadError}</p>
                    </div>
                  )}

                  {/* Parsed results summary bar */}
                  {prequalData?.workbookParsedAt && !prequalData?.workbookParseFailed && (
                    <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-gray-50 to-indigo-50 border border-gray-200 px-5 py-3.5">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                          prequalData.prequal_result === 'Approved' ? 'bg-green-100 text-green-800 ring-1 ring-green-600/20'
                          : prequalData.prequal_result === 'Conditional' ? 'bg-orange-100 text-orange-800 ring-1 ring-orange-600/20'
                          : 'bg-red-100 text-red-800 ring-1 ring-red-600/20'
                        }`}>{prequalData.prequal_result}</span>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>DSCR 2024: <strong className="text-gray-900">{typeof prequalData.dscr_2024 === 'number' ? prequalData.dscr_2024.toFixed(2) : 'N/A'}</strong></span>
                          <span className="text-gray-300">|</span>
                          <span>Interim: <strong className="text-gray-900">{typeof prequalData.dscr_interim === 'number' ? prequalData.dscr_interim.toFixed(2) : 'N/A'}</strong></span>
                          <span className="text-gray-300">|</span>
                          <span>Loan: <strong className="text-gray-900">${prequalData.sbaLoanAmount?.toLocaleString() || 'N/A'}</strong></span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // Load ALL workbook data from prequalData
                          setParsedValues({ ...prequalData, _raw: true });
                          setShowParsedModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        View / Edit Results
                      </button>
                      <label className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-200 transition-colors cursor-pointer">
                        <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                        Re-upload Workbook
                        <input
                          type="file"
                          accept=".xlsx,.xls,.xlsm"
                          className="hidden"
                          disabled={workbookUploading || waitingForParse}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setWorkbookFile(file);
                            setWorkbookUploadError(null);
                            setWorkbookUploading(true);
                            setWorkbookUploadProgress(0);
                            const storageRef = ref(storage, `listings/${prequalId}/workbook/${file.name}`);
                            const uploadTask = uploadBytesResumable(storageRef, file);
                            uploadTask.on('state_changed',
                              (snapshot) => setWorkbookUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
                              (error) => { setWorkbookUploadError(error.message); setWorkbookUploading(false); },
                              async () => {
                                try {
                                  await getDownloadURL(uploadTask.snapshot.ref);
                                  const listingsDocRef = doc(db, 'listings', prequalId);
                                  await setDoc(listingsDocRef, {
                                    workbookFileUploaded: false, workbookParsedAt: null, workbookParseFailed: null, workbookParseError: null,
                                    recordType: 'prequal', prequalId,
                                    businessName: prequalData?.businessName || '',
                                    brokerEmail: prequalData?.brokerEmail || '',
                                    brokerFirstName: prequalData?.brokerFirstName || prequalData?.brokerName?.split(' ')[0] || '',
                                    uploadedAt: serverTimestamp(),
                                  }, { merge: true });
                                  await updateDoc(listingsDocRef, { workbookFileUploaded: true });
                                  await updateDoc(doc(db, 'prequalificationApplications', prequalId), { workbookFileUploaded: true, recordType: 'prequal' });
                                  setWorkbookUploading(false);
                                  setWaitingForParse(true);
                                  const unsubscribe = onSnapshot(listingsDocRef, (snap) => {
                                    const data = snap.data();
                                    if (data?.workbookParsedAt) { unsubscribe(); setWaitingForParse(false); setParsedValues({ ...data, _raw: true }); setShowParsedModal(true); }
                                    if (data?.workbookParseFailed) { unsubscribe(); setWaitingForParse(false); setWorkbookUploadError(data.workbookParseError || 'Parse failed'); }
                                  });
                                  setTimeout(() => { unsubscribe(); setWaitingForParse(false); }, 120000);
                                } catch (err) { setWorkbookUploadError(err.message); setWorkbookUploading(false); }
                              }
                            );
                          }}
                        />
                      </label>
                    </div>
                  )}
                  {(workbookUploading || waitingForParse) && (
                    <div className="mt-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>{waitingForParse ? 'Parsing workbook...' : 'Uploading...'}</span>
                        {!waitingForParse && <span>{workbookUploadProgress}%</span>}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className={`h-2.5 rounded-full transition-all duration-300 ${waitingForParse ? 'bg-yellow-500 animate-pulse' : 'bg-indigo-600'}`}
                          style={{ width: waitingForParse ? '100%' : `${workbookUploadProgress}%` }}></div>
                      </div>
                    </div>
                  )}
                  {workbookUploadError && <div className="mt-2 rounded-md bg-red-50 p-3"><p className="text-sm text-red-700">{workbookUploadError}</p></div>}
                </div>
              </div>
            </div>

            {/* Chosen Buyer Card - Only show if buyer is selected */}
            {prequalData.chosenBuyer && (
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 shadow-lg rounded-xl overflow-hidden border-2 border-amber-300">
                <div className="px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-200 mr-4">
                        <UserCircleIcon className="h-7 w-7 text-amber-700" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Chosen Buyer</p>
                        <p className="text-xl font-bold text-amber-900">
                          {prequalData.chosenBuyer.firstName} {prequalData.chosenBuyer.lastName}
                        </p>
                        <p className="text-sm text-amber-700">{prequalData.chosenBuyer.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-200 text-amber-800">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Selected
                      </span>
                      {prequalData.buyerSelectedAt && (
                        <p className="text-xs text-amber-600 mt-2">
                          Selected: {new Date(prequalData.buyerSelectedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Business Details */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Business Details</h3>
                </div>
                <button
                  onClick={toggleBusinessDetails}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isBusinessDetailsExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isBusinessDetailsExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Business name</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {prequalData.businessName || 'N/A'}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Description</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.businessDescription || 'No description provided.'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Purchase price
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {prequalData.purchasePrice ? `$${Number(prequalData.purchasePrice).toLocaleString()}` : 'N/A'}
                        {prequalData.realEstateIncluded && prequalData.purchasePrice && prequalData.realEstateValue &&
                        (() => {
                          const pp = parseFloat(prequalData.purchasePrice);
                          const rev = parseFloat(prequalData.realEstateValue);
                          if (!isNaN(pp) && !isNaN(rev)) {
                            return (
                              <div className="mt-1 pl-4 text-xs text-gray-700">
                                <p>Business Acquisition (Est.): {`$${(pp - rev).toLocaleString()}`}</p>
                                <p>Included Real Estate: {`$${rev.toLocaleString()}`}</p>
                              </div>
                            );
                          }
                          return null; 
                        })()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Business Age</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.businessAge || 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">License Needed</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.licenseNeeded === 'boolean' ? (prequalData.licenseNeeded ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Real Estate Included</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.realEstateIncluded === 'boolean' ? (prequalData.realEstateIncluded ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    {prequalData.realEstateIncluded && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Real Estate Value</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {prequalData.realEstateValue ? `$${Number(prequalData.realEstateValue).toLocaleString()}` : 'N/A'}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Has Multiple LLCs</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.hasMultipleLLCs === 'boolean' ? (prequalData.hasMultipleLLCs ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Has Other Addbacks</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.hasOtherAddbacks === 'boolean' ? (prequalData.hasOtherAddbacks ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Top Customer Concentration</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData.topCustomerConcentration === 'boolean' ? (prequalData.topCustomerConcentration ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created At</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.createdAt ? formatDate(prequalData.createdAt) : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData.lastUpdated ? formatDate(prequalData.lastUpdated) : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>

            {/* Financial Overview */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <BanknotesIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Financial Overview</h3>
                </div>
                <button
                  onClick={toggleFinancialOverview}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isFinancialOverviewExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isFinancialOverviewExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  {prequalData?.grossSales_2024 || prequalData?.grossSales_2022 ? (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">From Workbook</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-800 text-white">
                              <th className="px-3 py-2 text-left">Metric</th>
                              <th className="px-3 py-2 text-right">2022</th>
                              <th className="px-3 py-2 text-right">2023</th>
                              <th className="px-3 py-2 text-right">2024</th>
                              <th className="px-3 py-2 text-right">Interim</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[['Gross Sales', 'grossSales'], ['COGS', 'cogs'], ['Gross Profit', 'grossProfit'],
                              ['Officers Comp', 'officersComp'], ['Salaries & Wages', 'salariesWages'],
                              ['Interest', 'interest'], ['Depreciation', 'depreciation'],
                              ['Other G&A', 'otherGA'], ['Net Profit', 'netProfit'],
                            ].map(([label, field], idx) => {
                              const isBold = ['Gross Profit', 'Net Profit'].includes(label);
                              return (
                                <tr key={field} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isBold ? 'font-semibold border-t border-gray-300' : ''}`}>
                                  <td className="px-3 py-1.5 text-gray-700">{label}</td>
                                  {['2022', '2023', '2024', 'interim'].map(yr => (
                                    <td key={yr} className="px-3 py-1.5 text-right text-gray-900">
                                      {typeof prequalData[`${field}_${yr}`] === 'number' ? `$${prequalData[`${field}_${yr}`].toLocaleString()}` : '—'}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                            <tr className="bg-indigo-50 font-bold border-t-2 border-indigo-200">
                              <td className="px-3 py-2 text-indigo-800">EBITDA/R</td>
                              {['2022', '2023', '2024', 'interim'].map(yr => (
                                <td key={yr} className="px-3 py-2 text-right text-indigo-800">
                                  {typeof prequalData[`ebitdaR_${yr}`] === 'number' ? `$${prequalData[`ebitdaR_${yr}`].toLocaleString()}` : '—'}
                                </td>
                              ))}
                            </tr>
                            <tr className="bg-indigo-50 font-bold">
                              <td className="px-3 py-2 text-indigo-800">EBITDA+SDE</td>
                              {['2022', '2023', '2024', 'interim'].map(yr => (
                                <td key={yr} className="px-3 py-2 text-right text-indigo-800">
                                  {typeof prequalData[`ebitdaSde_${yr}`] === 'number' ? `$${prequalData[`ebitdaSde_${yr}`].toLocaleString()}` : '—'}
                                </td>
                              ))}
                            </tr>
                            <tr className="bg-green-50 font-bold border-t-2 border-green-200">
                              <td className="px-3 py-2 text-green-800">DSCR (EBITDA+SDE)</td>
                              {['2022', '2023', '2024', 'interim'].map(yr => {
                                const v = prequalData[`dscr_sde_${yr}`] || prequalData[`dscr_${yr === '2024' ? '2024' : yr === 'interim' ? 'interim' : ''}`];
                                const color = typeof v === 'number' ? (v >= 1.3 ? 'text-green-700' : v >= 1.1 ? 'text-orange-600' : 'text-red-600') : 'text-gray-400';
                                return (
                                  <td key={yr} className={`px-3 py-2 text-right font-bold ${color}`}>
                                    {typeof v === 'number' ? `${v.toFixed(2)}x` : '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {/* Sources & Uses summary */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[['SBA Loan', prequalData.sbaLoanAmount], ['Purchase Price', prequalData.purchasePrice],
                          ['Equity', prequalData.borrowerEquity], ['Annual Debt Svc', prequalData.annualDebtService]
                        ].map(([label, val]) => (
                          <div key={label} className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                            <p className="text-xs text-gray-500">{label}</p>
                            <p className="text-sm font-bold text-gray-900">{typeof val === 'number' ? `$${val.toLocaleString()}` : '—'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <BanknotesIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No financial data available. Upload a workbook or tax returns to populate.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Prequalification Table */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100" id="prequalification-table">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <FunnelIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Prequalification Analysis</h3>
                </div>
                <button
                  id="toggle-lock-button"
                  onClick={togglePrequalTableLock}
                  className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${isPrequalTableLocked ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 ${isPrequalTableLocked ? 'focus:ring-indigo-500' : 'focus:ring-green-500'}`}
                >
                  {isPrequalTableLocked ? (
                    <>
                      <LockOpenIcon className="h-4 w-4 mr-1" />
                      Unlock Selection
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-4 w-4 mr-1" />
                      Lock Selection
                    </>
                  )}
                </button>
              </div>
              <div className="border-t border-gray-100 px-6 py-5 overflow-x-auto">
                <PrequalificationTable 
                  ocrData={ocrData} 
                  loanData={prequalData || {}}
                  isLocked={isPrequalTableLocked}
                  onUnlockRequest={handleRequestUnlock}
                />
              </div>
            </div>

            {/* Uploaded Files */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <DocumentIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Uploaded Files</h3>
                </div>
                <button
                  onClick={toggleUploadedFiles}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isUploadedFilesExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isUploadedFilesExpanded && (
                <div className="border-t border-gray-100">
                  {uploadedFiles && uploadedFiles.length > 0 ? (
                    <div className="p-6">
                      {/* Sort and group files by documentName and then by label */}
                      {(() => {
                        // Sort files by documentName first, then by label
                        const sortedFiles = [...uploadedFiles].sort((a, b) => {
                          // First sort by documentName
                          const docNameA = (a.documentName || '').toLowerCase();
                          const docNameB = (b.documentName || '').toLowerCase();
                          
                          if (docNameA !== docNameB) {
                            return docNameA.localeCompare(docNameB);
                          }
                          
                          // If document names are equal, sort by label
                          const labelA = (a.label || '').toLowerCase();
                          const labelB = (b.label || '').toLowerCase();
                          return labelA.localeCompare(labelB);
                        });
                        
                        // Group files by documentName
                        const groupedByDocName = sortedFiles.reduce((groups, file) => {
                          const docName = file.documentName || 'Other Documents';
                          if (!groups[docName]) {
                            groups[docName] = [];
                          }
                          groups[docName].push(file);
                          return groups;
                        }, {});
                        
                        // For each documentName group, further group by label
                        const groupedFiles = {};
                        Object.entries(groupedByDocName).forEach(([docName, files]) => {
                          groupedFiles[docName] = files.reduce((labelGroups, file) => {
                            const label = file.label || 'Unlabeled';
                            if (!labelGroups[label]) {
                              labelGroups[label] = [];
                            }
                            labelGroups[label].push(file);
                            return labelGroups;
                          }, {});
                        });
                        
                        // Render grouped files
                        return Object.entries(groupedFiles).map(([docName, labelGroups]) => (
                          <div key={docName} className="mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
                              {docName}
                            </h3>
                            
                            {Object.entries(labelGroups).map(([label, files]) => (
                              <div key={`${docName}-${label}`} className="mb-6">
                                <h4 className="text-md font-medium text-gray-700 mb-3 pl-2 border-l-4 border-indigo-400">
                                  {label}
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {files.map((file, index) => (
                                    <div 
                                      key={file.id || file.fileId || `${docName}-${label}-${index}`} 
                                      className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                                    >
                                      <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-indigo-50 rounded-lg">
                                          <DocumentIcon className="h-5 w-5 text-indigo-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">
                                            {file.fileName || file.name || 'Unnamed File'}
                                          </p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            {formatDate(file.uploadedAt || file.createdAt || '')}
                                          </p>
                                          <div className="mt-2">
                                            <a
                                              href={file.fileUrl || file.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                            >
                                              View Document
                                            </a>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <DocumentIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No files uploaded yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Parsed Results Review Modal */}
            {showParsedModal && parsedValues && (() => {
              const v = parsedValues;
              const fmt = (n) => typeof n === 'number' ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '';
              const fmtDscr = (n) => typeof n === 'number' ? n.toFixed(2) : 'N/A';
              const dscrColor = (n) => typeof n === 'number' ? (n >= 1.3 ? 'bg-green-100 text-green-800' : n >= 1.1 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800') : '';
              const EditCell = ({ field }) => (
                <input type="number" step="any" value={v[field] ?? ''} onChange={(e) => setParsedValues(p => ({ ...p, [field]: parseFloat(e.target.value) || 0 }))}
                  className="w-full text-xs text-right border-gray-300 rounded px-1 py-0.5" />
              );
              const ReadCell = ({ field }) => <span className="text-xs text-right block px-1">{fmt(v[field])}</span>;
              const years = ['2022', '2023', '2024', 'interim'];

              return (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-3 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-lg bg-white" style={{ maxHeight: '94vh', overflowY: 'auto' }}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Workbook Parsed Results — Review & Edit</h3>
                    <button onClick={() => setShowParsedModal(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                  </div>

                  {/* Top summary row */}
                  <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Result:</span>
                      <select value={v.prequal_result || 'Pending'} onChange={(e) => setParsedValues(p => ({ ...p, prequal_result: e.target.value }))}
                        className={`rounded-md text-sm font-bold px-2 py-1 ${v.prequal_result === 'Approved' ? 'bg-green-100 text-green-800' : v.prequal_result === 'Conditional' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                        <option value="Approved">Approved</option><option value="Conditional">Conditional</option><option value="Declined">Declined</option><option value="Pending">Pending</option>
                      </select>
                    </div>
                    <div className={`text-sm font-bold px-3 py-1 rounded ${dscrColor(v.dscr_2024)}`}>DSCR 2024: {fmtDscr(v.dscr_2024)}</div>
                    <div className={`text-sm font-bold px-3 py-1 rounded ${dscrColor(v.dscr_interim)}`}>DSCR Interim: {fmtDscr(v.dscr_interim)}</div>
                    <div className="text-sm"><strong>Prequal Amount: ${fmt(v.purchasePrice)}</strong></div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* LEFT: Sources & Uses */}
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 mb-1 border-b pb-1 uppercase">Sources & Uses</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {[['SBA Loan', 'sbaLoanAmount'], ['Purchase Price', 'purchasePrice'], ['Borrower Equity', 'borrowerEquity'], ['Working Capital', 'workingCapital'],
                          ['Guaranty Fee', 'sbaGuarantyFee'], ['Closing Costs', 'closingCosts'], ['Interest Rate', 'totalInterestRate'], ['Term (months)', 'termMonths']
                        ].map(([label, key]) => (
                          <div key={key}>
                            <label className="text-gray-500">{label}</label>
                            <EditCell field={key} />
                          </div>
                        ))}
                      </div>

                      {/* DSCR & EBITDA+SDE */}
                      <h4 className="text-xs font-bold text-gray-800 mt-3 mb-1 border-b pb-1 uppercase">DSCR & Cash Flow</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {[['DSCR 2024', 'dscr_2024'], ['DSCR Interim', 'dscr_interim'],
                          ['EBITDA 2024', 'ebitdaR_2024'], ['EBITDA Interim', 'ebitdaR_interim'],
                          ['EBITDA+SDE 2024', 'ebitdaSde_2024'], ['EBITDA+SDE Interim', 'ebitdaSde_interim'],
                          ['SDE 2024', 'sde_2024'], ['SDE Interim', 'sde_interim'],
                          ['Annual Debt Svc', 'annualDebtService'], ['Monthly PMT', 'monthlyPmt'],
                        ].map(([label, key]) => (
                          <div key={key}>
                            <label className="text-gray-500">{label}</label>
                            <EditCell field={key} />
                          </div>
                        ))}
                      </div>

                      {/* Guarantor */}
                      {v.guarantor_requiredDraw != null && (
                        <>
                          <h4 className="text-xs font-bold text-gray-800 mt-3 mb-1 border-b pb-1 uppercase">Guarantor Analysis</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {[['Required Draw', 'guarantor_requiredDraw'], ['Min Draw', 'guarantor_minDraw'],
                              ['Cash', 'guarantor_cash'], ['Savings', 'guarantor_savings'],
                              ['Retirement', 'guarantor_retirement'], ['Real Estate', 'guarantor_realEstate'],
                              ['Spouse Salary', 'guarantor_spouseSalary'], ['Home Payment', 'guarantor_homePayment'],
                            ].map(([label, key]) => (
                              <div key={key}>
                                <label className="text-gray-500">{label}</label>
                                <EditCell field={key} />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* RIGHT: Full workbook breakdown */}
                    <div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="bg-gray-800 text-white">
                              <th className="px-2 py-1 text-left">Metric</th>
                              {years.map(y => <th key={y} className="px-1 py-1 text-right">{y === 'interim' ? 'Interim' : y}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {/* --- INCOME STATEMENT --- */}
                            <tr className="bg-gray-100"><td colSpan="5" className="px-2 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Income Statement</td></tr>
                            {[['Gross Sales', 'grossSales'], ['COGS', 'cogs'], ['Other Income', 'otherIncome']
                            ].map(([label, field], idx) => (
                              <tr key={field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-2 py-0.5 text-gray-700 whitespace-nowrap">{label}</td>
                                {years.map(yr => <td key={yr} className="px-0.5 py-0.5"><EditCell field={`${field}_${yr}`} /></td>)}
                              </tr>
                            ))}
                            <tr className="bg-blue-50 font-semibold border-t"><td className="px-2 py-0.5 text-blue-800">Gross Profit</td>
                              {years.map(yr => <td key={yr} className="px-1 py-0.5"><ReadCell field={`grossProfit_${yr}`} /></td>)}
                            </tr>
                            {[['Officers Comp', 'officersComp'], ['Salaries/Wages', 'salariesWages'], ['Repairs', 'repairs'],
                              ['Bad Debts', 'badDebts'], ['Rents', 'rents'], ['Taxes/Licenses', 'taxesLicenses'],
                              ['Interest', 'interest'], ['Depreciation', 'depreciation'], ['Depletion', 'depletion'],
                              ['Advertising', 'advertising'], ['Post Retirement', 'postRetirement'], ['Other G&A', 'otherGA'],
                            ].map(([label, field], idx) => (
                              <tr key={field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-2 py-0.5 text-gray-700 whitespace-nowrap">{label}</td>
                                {years.map(yr => <td key={yr} className="px-0.5 py-0.5"><EditCell field={`${field}_${yr}`} /></td>)}
                              </tr>
                            ))}
                            <tr className="bg-blue-50 font-semibold border-t"><td className="px-2 py-0.5 text-blue-800">Total Expenses</td>
                              {years.map(yr => <td key={yr} className="px-1 py-0.5"><ReadCell field={`totalExpenses_${yr}`} /></td>)}
                            </tr>
                            <tr className="bg-indigo-50 font-bold border-t"><td className="px-2 py-1 text-indigo-800">Net Profit</td>
                              {years.map(yr => <td key={yr} className="px-1 py-1"><ReadCell field={`netProfit_${yr}`} /></td>)}
                            </tr>

                            {/* --- EBITDA BUILD-UP --- */}
                            <tr className="bg-gray-100"><td colSpan="5" className="px-2 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">EBITDA Build-Up</td></tr>
                            {[['+ Rents', 'addRents'], ['+ Bad Debt', 'addBadDebt'], ['+ Interest', 'addInterest'],
                              ['+ Depreciation', 'addDepreciation'], ['+ Depletion', 'addDepletion'], ['+ Amortization', 'addAmortization'],
                            ].map(([label, field]) => (
                              <tr key={field} className="bg-white">
                                <td className="px-2 py-0.5 text-gray-500 whitespace-nowrap text-[11px]">{label}</td>
                                {years.map(yr => <td key={yr} className="px-1 py-0.5"><ReadCell field={`${field}_${yr}`} /></td>)}
                              </tr>
                            ))}
                            <tr className="bg-indigo-50 font-bold border-t"><td className="px-2 py-1 text-indigo-800">EBITDA/R</td>
                              {years.map(yr => <td key={yr} className="px-1 py-1"><ReadCell field={`ebitdaR_${yr}`} /></td>)}
                            </tr>

                            {/* --- SDE ADJUSTMENTS --- */}
                            <tr className="bg-gray-100"><td colSpan="5" className="px-2 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">SDE Adjustments</td></tr>
                            {[['+ Owner Salary', 'ownerSalaryAddback'],
                              ['- New Salary (required)', 'newSalaryRequired'],
                              ['+ Fed Taxes', 'addFedTaxes'],
                              ['+ Misc SDEs', 'miscSdesRef'],
                            ].map(([label, field]) => (
                              <tr key={field} className="bg-white">
                                <td className="px-2 py-0.5 text-gray-500 whitespace-nowrap text-[11px]">{label}</td>
                                {years.map(yr => <td key={yr} className="px-1 py-0.5"><ReadCell field={`${field}_${yr}`} /></td>)}
                              </tr>
                            ))}
                            <tr className="bg-purple-50 font-semibold border-t"><td className="px-2 py-0.5 text-purple-800">SDE</td>
                              {years.map(yr => <td key={yr} className="px-1 py-0.5"><ReadCell field={`sde_${yr}`} /></td>)}
                            </tr>
                            <tr className="bg-indigo-100 font-bold"><td className="px-2 py-1 text-indigo-900">EBITDA + SDE</td>
                              {years.map(yr => <td key={yr} className="px-1 py-1"><ReadCell field={`ebitdaSde_${yr}`} /></td>)}
                            </tr>
                            <tr className="bg-green-50 font-bold"><td className="px-2 py-1 text-green-800">DSCR (EBITDA+SDE)</td>
                              {years.map(yr => {
                                const dVal = v[`dscrIncludingSdes_${yr}`] || v[`dscr_sde_${yr}`];
                                const color = typeof dVal === 'number' ? (dVal >= 1.3 ? 'text-green-700' : dVal >= 1.1 ? 'text-orange-600' : 'text-red-600') : 'text-gray-400';
                                return <td key={yr} className={`px-1 py-1 text-right text-xs font-bold ${color}`}>{typeof dVal === 'number' ? `${dVal.toFixed(2)}x` : '—'}</td>;
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Itemized SDE Add-backs */}
                      {v.sdeAddbacks && v.sdeAddbacks.length > 0 && (
                        <>
                          <h4 className="text-xs font-bold text-gray-800 mt-3 mb-1 border-b pb-1 uppercase">Itemized Add-backs (detail for Misc SDEs)</h4>
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="px-2 py-1 text-left">Description</th>
                                {years.map(y => <th key={y} className="px-1 py-1 text-right">{y === 'interim' ? 'Int' : y}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {v.sdeAddbacks.map((ab, idx) => (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-2 py-0.5 text-gray-700 truncate" style={{ maxWidth: '150px' }} title={ab.label}>{ab.label}</td>
                                  {years.map(yr => <td key={yr} className="px-1 py-0.5 text-right">{fmt(ab[yr])}</td>)}
                                </tr>
                              ))}
                              <tr className="bg-yellow-50 font-semibold border-t">
                                <td className="px-2 py-0.5 text-yellow-800">Total Add-backs</td>
                                {years.map(yr => <td key={yr} className="px-1 py-0.5 text-right">{fmt(v[`sdeAddbacksTotal_${yr}`])}</td>)}
                              </tr>
                            </tbody>
                          </table>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                    <button onClick={() => setShowParsedModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                    <button
                      disabled={savingParsedValues}
                      onClick={async () => {
                        setSavingParsedValues(true);
                        try {
                          const vals = { ...parsedValues };
                          // Remove internal/non-serializable fields
                          delete vals._raw;
                          // Remove Firestore Timestamp objects (have .toDate method) but keep arrays and plain objects
                          Object.keys(vals).forEach(k => {
                            const v = vals[k];
                            if (v && typeof v === 'object' && !Array.isArray(v) && typeof v.toDate === 'function') delete vals[k];
                            // Remove fields starting with _ (internal debug fields)
                            if (k.startsWith('_')) delete vals[k];
                          });

                          // Prequal amount = total project cost (purchase + gty fee + closing costs)
                          const prequalAmount = (Number(vals.purchasePrice) || 0) + (Number(vals.sbaGuarantyFee) || 0) + (Number(vals.closingCosts) || 0) + (Number(vals.workingCapital) || 0);

                          const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
                          await updateDoc(prequalDocRef, {
                            ...vals,
                            workbookParsedAt: serverTimestamp(),
                            prequalifiedAmount: prequalAmount,
                            expectedPrequalAmount: prequalAmount,
                            borrowerPrequalAmount: prequalAmount,
                            status: 'prequalified',
                            prequalifiedAt: serverTimestamp(),
                            lastUpdated: serverTimestamp(),
                          });
                          const listingsDocRef = doc(db, 'listings', prequalId);
                          await setDoc(listingsDocRef, { ...vals, workbookParsedAt: serverTimestamp(), prequalifiedAmount: prequalAmount }, { merge: true });

                          // Send broker email — look up from broker doc if needed
                          let brokerEmail = prequalData?.brokerEmail;
                          let brokerFirst = prequalData?.brokerFirstName || prequalData?.brokerName?.split(' ')[0] || '';
                          const bizName = prequalData?.businessName || vals.businessName || 'Business';
                          if (!brokerEmail && prequalData?.brokerId) {
                            try {
                              const brokerSnap = await getDoc(doc(db, 'brokers', prequalData.brokerId));
                              if (brokerSnap.exists()) {
                                const bd = brokerSnap.data();
                                brokerEmail = bd.email;
                                brokerFirst = brokerFirst || bd.firstName || bd.name?.split(' ')[0] || '';
                              }
                            } catch (e) { console.error('Error fetching broker for email:', e); }
                          }
                          if (!brokerEmail) {
                            alert('No broker email found. Add a broker to this listing to send the prequal email.');
                          }
                          if (brokerEmail) {
                            try {
                              const fmtAmt = (n) => typeof n === 'number' ? '$' + n.toLocaleString() : 'N/A';
                              const dscrClr = (v) => !v ? '#666' : v >= 1.3 ? '#16a34a' : v >= 1.1 ? '#ea580c' : '#dc2626';
                              const result = vals.prequal_result || 'Pending';
                              const badgeStyle = result === 'Approved' ? 'background:#dcfce7;color:#166534;' : result === 'Conditional' ? 'background:#fff7ed;color:#9a3412;' : 'background:#fef2f2;color:#991b1b;';

                              // Generate the prequal PDF to attach
                              let pdfUrl = null;
                              try {
                                const pdfContent = {
                                  businessName: bizName,
                                  prequalifiedAmount: prequalAmount,
                                  prequalificationType: vals.prequalificationType || 'Adjusted Purchase Price',
                                  prequalificationEquityPercent: 10,
                                  brokerName: prequalData?.brokerName || brokerFirst,
                                  realEstateValue: prequalData?.realEstateValue || 0,
                                  ocrData: ocrData || {},
                                  prequalId: prequalId
                                };
                                const pdfBlob = await generatePrequalSummaryPDF(pdfContent);
                                if (pdfBlob) {
                                  const pdfFileName = `${bizName}_Prequalification_Summary.pdf`;
                                  const pdfStorageRef = ref(storage, `prequalifications/${prequalId}/summaries/${pdfFileName}`);
                                  await uploadBytesResumable(pdfStorageRef, pdfBlob);
                                  pdfUrl = await getDownloadURL(pdfStorageRef);
                                }
                              } catch (pdfErr) {
                                console.error('Error generating PDF for email:', pdfErr);
                              }

                              const emailRef = collection(db, 'email');
                              const emailData = {
                                to: brokerEmail,
                                from: 'Isak @ Lended <isak@lended.ai>',
                                replyTo: 'isak@lended.ai',
                                subject: `${bizName} — Pre-Qualified for ${fmtAmt(prequalAmount)}`,
                                message: {
                                  subject: `${bizName} — Pre-Qualified for ${fmtAmt(prequalAmount)}`,
                                  html: `
<p>Hi ${brokerFirst},</p>
<p>${bizName} has been pre-qualified. Here are the details:</p>
<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin:16px 0;">
  <table style="width:100%;border-collapse:collapse;">
    <tr><td style="padding:8px 0;color:#64748b;">Result</td><td style="padding:8px 0;text-align:right;"><span style="${badgeStyle}padding:4px 12px;border-radius:20px;font-weight:bold;">${result}</span></td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:8px 0;color:#64748b;">Prequalified Amount</td><td style="padding:8px 0;text-align:right;font-weight:bold;font-size:18px;color:#1e3a5f;">${fmtAmt(prequalAmount)}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:8px 0;color:#64748b;">SBA Loan Amount</td><td style="padding:8px 0;text-align:right;font-weight:bold;">${fmtAmt(vals.sbaLoanAmount)}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:8px 0;color:#64748b;">Purchase Price</td><td style="padding:8px 0;text-align:right;font-weight:bold;">${fmtAmt(vals.purchasePrice)}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:8px 0;color:#64748b;">Borrower Equity (10%)</td><td style="padding:8px 0;text-align:right;font-weight:bold;">${fmtAmt(vals.borrowerEquity)}</td></tr>
    ${typeof vals.dscr_2024 === 'number' ? `<tr style="border-top:1px solid #e2e8f0;"><td style="padding:8px 0;color:#64748b;">DSCR 2024</td><td style="padding:8px 0;text-align:right;font-weight:bold;color:${dscrClr(vals.dscr_2024)};">${vals.dscr_2024.toFixed(2)}x</td></tr>` : ''}
    ${typeof vals.dscr_interim === 'number' ? `<tr style="border-top:1px solid #e2e8f0;"><td style="padding:8px 0;color:#64748b;">DSCR Interim</td><td style="padding:8px 0;text-align:right;font-weight:bold;color:${dscrClr(vals.dscr_interim)};">${vals.dscr_interim.toFixed(2)}x</td></tr>` : ''}
  </table>
</div>
${pdfUrl ? `<p><a href="${pdfUrl}" style="display:inline-block;padding:10px 24px;background:#4f46e5;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">Download Prequalification Summary PDF</a></p>` : ''}
<p>You can access the listing on your broker portal:<br><a href="https://lended.ai/broker-portal" style="color:#4f46e5;font-weight:bold;">https://lended.ai/broker-portal</a></p>
<h3 style="color:#1e3a5f;margin-top:24px;">Next Steps</h3>
<ol style="line-height:1.8;">
  <li>Share the searcher interest link with interested buyers:<br><a href="https://lended.ai/prequal-signup/${prequalId}" style="color:#4f46e5;">https://lended.ai/prequal-signup/${prequalId}</a></li>
  <li>Begin gathering intake documents (business debt schedule, articles of org, operating agreement, AR/AP aging, etc.)</li>
</ol>
<p>Let me know if you have any questions.</p>
<p>Best,</p>
<p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>`,
                                },
                              };
                              // Attach PDF if available
                              if (pdfUrl) {
                                emailData.attachments = [{
                                  filename: `${bizName}_Prequalification_Summary.pdf`,
                                  path: pdfUrl,
                                }];
                              }
                              await addDoc(emailRef, emailData);
                              console.log('Broker prequal email sent to:', brokerEmail);
                            } catch (emailErr) {
                              console.error('Error sending broker email:', emailErr);
                            }
                          }

                          const updatedDoc = await getDoc(prequalDocRef);
                          if (updatedDoc.exists()) setPrequalData({ id: updatedDoc.id, ...updatedDoc.data() });
                          setShowParsedModal(false);
                          alert('Saved! Prequal amount: $' + prequalAmount.toLocaleString() + (brokerEmail ? '\nEmail sent to broker: ' + brokerEmail : ''));
                        } catch (err) {
                          alert('Error saving: ' + err.message);
                        } finally { setSavingParsedValues(false); }
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"
                    >{savingParsedValues ? 'Saving...' : 'Confirm & Save to Prequal'}</button>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* OCR Review */}
            {prequalId && (
              <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
                <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    <h3 className="text-lg font-semibold leading-7 text-gray-900">OCR Document Review</h3>
                  </div>
                </div>
                <div className="border-t border-gray-100">
                  <PrequalificationOcrReview 
                    prequalId={prequalId} 
                    uploadedFiles={uploadedFiles}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrequalificationDetails;
